import { useRef, useState } from "react";
import { api, errText } from "@/lib/api";
import { uploadVideoInChunks } from "@/lib/upload";
import { videoFileSrc, fmtBytes } from "@/lib/media";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, Link2, Trash2 } from "lucide-react";

export const MediaPreview = ({ media, square, poster }) => {
  const src = videoFileSrc(media);
  const box = `relative bg-black overflow-hidden ${square ? "aspect-square" : "aspect-video"}`;
  if (src) {
    return (
      <div className={box}>
        <video src={src} poster={poster} controls muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={box}>
      <iframe src={media.embed_url} title="Video preview" allow="autoplay; fullscreen" className="absolute inset-0 w-full h-full" />
    </div>
  );
};

// One configurable video slot: upload a file (chunked) OR paste a YouTube / Vimeo / .mp4 link.
export const MediaSlot = ({ slot, label, hint, value, onChange, square, poster }) => {
  const [progress, setProgress] = useState(null);
  const [link, setLink] = useState("");
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      onChange(await uploadVideoInChunks(file, slot, setProgress));
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveLink = async () => {
    try {
      const { data } = await api.put(`/media/slots/${slot}`, { url: link });
      onChange(data.value);
      setLink("");
      toast.success(`${label} link saved`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async () => {
    await api.delete(`/media/slots/${slot}`);
    onChange(null);
    toast.success(`${label} removed`);
  };

  return (
    <div className="border border-gray-200 p-4 space-y-4" data-testid={`media-slot-${slot}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</p>
          {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
        </div>
        {value && (
          <button data-testid={`media-remove-${slot}`} onClick={remove} className="text-gray-400 hover:text-red-600 transition-colors duration-150" title="Remove">
            <Trash2 size={15} />
          </button>
        )}
      </div>

      {value ? (
        <div className="space-y-2">
          <MediaPreview media={value} square={square} poster={poster} />
          <p className="text-xs text-gray-500 truncate" data-testid={`media-current-${slot}`}>
            {value.source === "upload" ? `${value.filename} · ${fmtBytes(value.size)}` : value.url}
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-400" data-testid={`media-empty-${slot}`}>No video yet.</p>
      )}

      {progress !== null ? (
        <div className="space-y-1.5" data-testid={`media-progress-${slot}`}>
          <Progress value={progress} className="h-1.5" />
          <p className="text-xs text-gray-500">{progress < 100 ? `Uploading… ${progress}%` : "Saving…"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-xs uppercase tracking-wider border border-gray-300 h-10 px-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150 w-fit">
            <Upload size={13} /> {value ? "Replace with a file" : "Upload video file"}
            <input ref={fileRef} data-testid={`media-file-${slot}`} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.mov,.webm,.m4v" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <div className="flex gap-2">
            <Input data-testid={`media-link-${slot}`} placeholder="…or paste a YouTube, Vimeo or .mp4 link" value={link} onChange={(e) => setLink(e.target.value)} className="rounded-sm h-10 text-sm" />
            <Button data-testid={`media-link-save-${slot}`} onClick={saveLink} disabled={!link.trim()} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider shrink-0">
              <Link2 size={13} className="mr-1.5" /> Use link
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
